import {
  ChartColumn,
  Check,
  ChevronDown,
  CircleCheck,
  CircleDot,
  GripVertical,
  ListOrdered,
  Plus,
  Star,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';
import { useState } from 'react';

import type { ResearchPollFormat } from '@repo/contracts';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@repo/ui/menu';

import { MAX_OPTIONS } from './poll-draft';
import { POLL_TYPES, pollTitle } from './poll-type-picker';

export interface PollDraftState {
  format: ResearchPollFormat;
  options: string[];
  /** Once the researcher edits an option by hand, the question text stops
   *  rewriting them. */
  touched: boolean;
}

/** As designed: a poll's question fits on a card. */
export const MAX_POLL_QUESTION = 140;

const MENU_ICON: Record<ResearchPollFormat, typeof CircleDot> = {
  choice: CircleDot,
  ranking: ListOrdered,
  rating: CircleDot,
  'yes-no': CircleCheck,
};

const PLACEHOLDER: Record<ResearchPollFormat, string> = {
  choice: 'E.g. Which hero makes it easiest to start a trial?',
  ranking: 'What should we fix first?',
  rating: 'How clear is the pricing page?',
  'yes-no': 'Is it clear where the free trial starts?',
};

const EXPLAINER: Record<ResearchPollFormat, string> = {
  choice: 'Personas choose one.',
  ranking:
    'Personas put these in order. The room’s ranking comes back with reasons.',
  rating: 'Each persona gives a score. You get the average.',
  'yes-no': 'Each persona answers Yes or No — and how sure they are.',
};

const LETTERS = 'ABCDE';

const FIELD =
  'rounded-card bg-warm text-ui-lg text-text-primary outline-none transition placeholder:text-text-muted focus-within:ring-2 focus-within:ring-brand/40';

const SECTION_LABEL =
  'text-tag font-semibold tracking-wide text-text-secondary uppercase';

/**
 * The poll, once its kind is picked: the question, what the personas choose
 * between, and Start. Everything the room votes on is on screen here first,
 * so the options are always ones the researcher saw and could fix.
 */
export function PollEditor({
  canStart,
  draft,
  onChange,
  onClose,
  onFormat,
  onQuestion,
  onStart,
  problem,
  question,
}: {
  /** False while the room is busy or the question is too short. */
  canStart: boolean;
  draft: PollDraftState;
  onChange: (options: string[]) => void;
  onClose: () => void;
  onFormat: (format: ResearchPollFormat) => void;
  onQuestion: (question: string) => void;
  onStart: () => void;
  /** Why it cannot be sent yet, shown once they try. */
  problem: string | null;
  question: string;
}) {
  return (
    <div aria-label="Poll" className="p-4" role="group">
      <div className="flex items-center gap-2">
        <h3 className="!m-0 min-w-0 flex-1 !text-ui-lg !leading-snug !font-semibold !text-text-primary">
          Poll Type — {pollTitle(draft.format)}
        </h3>
        <ChangePoll current={draft.format} onFormat={onFormat} />
        <button
          aria-label="Remove poll"
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-button text-text-secondary transition hover:bg-surface-hover hover:text-text-primary"
          onClick={onClose}
          type="button"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>

      <p className={`!mt-4 ${SECTION_LABEL}`}>Question</p>
      <label className={`mt-1.5 flex items-start gap-3 px-3.5 py-2.5 ${FIELD}`}>
        <textarea
          aria-label="Poll question"
          className="min-h-6 flex-1 resize-none bg-transparent leading-6 outline-none placeholder:text-text-muted"
          maxLength={MAX_POLL_QUESTION}
          onChange={(event) => {
            onQuestion(event.target.value);
          }}
          placeholder={PLACEHOLDER[draft.format]}
          rows={question.length > 44 ? 2 : 1}
          value={question}
        />
        <span className="shrink-0 pt-0.5 text-caption text-text-secondary tabular-nums">
          {question.length}/{MAX_POLL_QUESTION}
        </span>
      </label>

      <p className={`!mt-4 ${SECTION_LABEL}`}>
        {draft.format === 'rating'
          ? 'Scale'
          : draft.format === 'ranking'
            ? 'Items to rank'
            : 'Answers'}
      </p>
      <div className="mt-1.5">
        {draft.format === 'yes-no' ? (
          <YesNo />
        ) : draft.format === 'rating' ? (
          <Rating onChange={onChange} options={draft.options} />
        ) : (
          <Options
            format={draft.format}
            onChange={onChange}
            options={draft.options}
          />
        )}
      </div>

      <p className="mt-2 text-caption text-text-secondary">
        {EXPLAINER[draft.format]}
      </p>
      {problem ? (
        <p className="mt-1 text-caption text-critical" role="alert">
          {problem}
        </p>
      ) : null}

      <div className="mt-3 flex justify-end border-t border-hairline pt-3">
        <button
          className="flex h-10 cursor-pointer items-center gap-2 rounded-button bg-brand px-4 text-ui-lg font-semibold text-white transition hover:bg-brand-alt active:scale-[0.98] disabled:cursor-default disabled:bg-hairline disabled:text-text-muted"
          disabled={!canStart}
          onClick={onStart}
          type="button"
        >
          <ChartColumn aria-hidden="true" className="size-4" />
          Start Poll
        </button>
      </div>
    </div>
  );
}

function ChangePoll({
  current,
  onFormat,
}: {
  current: ResearchPollFormat;
  onFormat: (format: ResearchPollFormat) => void;
}) {
  return (
    <Menu>
      <MenuTrigger className="flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-pill bg-surface px-2.5 text-ui font-semibold text-text-primary ring-1 ring-hairline-alt transition hover:bg-surface-hover">
        <CircleDot aria-hidden="true" className="size-4 text-brand" />
        Change Poll
        <ChevronDown
          aria-hidden="true"
          className="size-3.5 text-text-secondary"
        />
      </MenuTrigger>
      <MenuContent className="w-80 rounded-card border-hairline bg-surface p-1.5 shadow-overlay">
        {POLL_TYPES.map((type) => {
          const Icon = MENU_ICON[type.format];
          const selected = type.format === current;
          return (
            <MenuItem
              className={[
                'cursor-pointer gap-3 rounded-button px-2.5 py-2',
                selected
                  ? 'bg-brand/10 data-highlighted:bg-brand/10'
                  : 'data-highlighted:bg-surface-hover',
              ].join(' ')}
              key={type.format}
              onClick={() => {
                if (!selected) onFormat(type.format);
              }}
            >
              <span
                className={[
                  'flex size-7 items-center justify-center rounded-button',
                  selected
                    ? 'bg-surface text-brand'
                    : 'bg-warm text-text-secondary',
                ].join(' ')}
              >
                <Icon aria-hidden="true" className="size-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-ui font-semibold text-text-primary">
                  {type.title}
                </span>
                <span className="block text-caption text-text-secondary">
                  {type.menuBlurb}
                </span>
              </span>
              {selected ? (
                <Check aria-hidden="true" className="size-4 text-brand" />
              ) : null}
            </MenuItem>
          );
        })}
      </MenuContent>
    </Menu>
  );
}

function YesNo() {
  return (
    <div className="flex flex-col gap-2">
      <span className={`flex items-center gap-3 px-3.5 py-2.5 ${FIELD}`}>
        <ThumbsUp aria-hidden="true" className="size-4 text-success-ink" />
        Yes
      </span>
      <span className={`flex items-center gap-3 px-3.5 py-2.5 ${FIELD}`}>
        <ThumbsDown aria-hidden="true" className="size-4 text-critical" />
        No
      </span>
    </div>
  );
}

/** Five levels; the ends are what the researcher words. */
function Rating({
  onChange,
  options,
}: {
  onChange: (options: string[]) => void;
  options: string[];
}) {
  const end = (index: 0 | 4, value: string) => {
    onChange(options.map((o, i) => (i === index ? value : o)));
  };
  return (
    <>
      <div aria-hidden="true" className="grid grid-cols-5 gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            className="flex flex-col items-center gap-1 rounded-button bg-warm py-2 text-ui font-semibold text-text-secondary"
            key={n}
          >
            <Star className="size-4 text-text-muted" />
            {n}
          </span>
        ))}
      </div>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        {([0, 4] as const).map((index) => (
          <label className={`px-3 py-2 ${FIELD}`} key={index}>
            <span className="block text-caption text-text-secondary">
              {index + 1} means
            </span>
            <input
              aria-label={`${String(index + 1)} means`}
              className="w-full bg-transparent text-ui-lg outline-none placeholder:text-text-muted"
              maxLength={80}
              onChange={(event) => {
                end(index, event.target.value);
              }}
              placeholder={index === 0 ? 'Not clear at all' : 'Crystal clear'}
              value={options[index] ?? ''}
            />
          </label>
        ))}
      </div>
    </>
  );
}

/** Preference answers (A, B…) or items to rank (1, 2…, draggable). */
function Options({
  format,
  onChange,
  options,
}: {
  format: 'choice' | 'ranking';
  onChange: (options: string[]) => void;
  options: string[];
}) {
  const ranking = format === 'ranking';
  const [dragged, setDragged] = useState<number | null>(null);

  const move = (from: number, to: number) => {
    if (from === to) return;
    const next = [...options];
    const [item] = next.splice(from, 1);
    if (item === undefined) return;
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <ol className="flex flex-col gap-2">
      {options.map((option, i) => (
        <li
          className={[
            `flex items-center gap-3 py-2 pr-2 pl-2.5 ${FIELD}`,
            dragged === i ? 'opacity-50' : '',
          ].join(' ')}
          draggable={ranking}
          key={i}
          onDragEnd={() => {
            setDragged(null);
          }}
          onDragOver={(event) => {
            if (dragged === null) return;
            event.preventDefault();
            if (dragged !== i) {
              move(dragged, i);
              setDragged(i);
            }
          }}
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = 'move';
            setDragged(i);
          }}
        >
          <span className="flex size-7 shrink-0 items-center justify-center rounded-button bg-surface text-ui font-semibold text-text-secondary ring-1 ring-hairline-alt">
            {ranking ? i + 1 : LETTERS[i]}
          </span>
          <input
            aria-label={`Option ${String(i + 1)}`}
            className="h-8 min-w-0 flex-1 bg-transparent outline-none placeholder:text-text-muted"
            maxLength={80}
            onChange={(event) => {
              onChange(
                options.map((o, j) => (j === i ? event.target.value : o)),
              );
            }}
            placeholder="An option"
            value={option}
          />
          {ranking ? (
            <GripVertical
              aria-hidden="true"
              className="size-4 shrink-0 cursor-grab text-text-muted"
            />
          ) : options.length > 1 ? (
            <button
              aria-label={`Remove option ${String(i + 1)}`}
              className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-button text-text-secondary transition hover:bg-surface hover:text-text-primary"
              onClick={() => {
                onChange(options.filter((_, j) => j !== i));
              }}
              type="button"
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          ) : null}
        </li>
      ))}
      {options.length < MAX_OPTIONS ? (
        <li>
          <button
            className="flex h-8 cursor-pointer items-center gap-2 rounded-button px-1 text-ui-lg font-semibold text-text-secondary transition hover:text-text-primary"
            onClick={() => {
              onChange([...options, '']);
            }}
            type="button"
          >
            <Plus aria-hidden="true" className="size-4" />
            {ranking ? 'Add another item' : 'Add another answer'}
          </button>
        </li>
      ) : null}
    </ol>
  );
}
